import React from 'react';
import {
  Users, CheckCircle, TrendingUp, Wifi, CreditCard, Building2,
  Layers, MapPin, AlertCircle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

const PERIOD_COLORS = ['#0d9488', '#3b82f6', '#f59e0b', '#ef4444'];

// ─── KPI Comparison Card ──────────────────
const KpiCompare = ({ title, icon: Icon, periods, accessor, formatter }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`tc-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={15} className="text-gray-500" />
      <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{title}</span>
    </div>
    <div className="flex items-end gap-4">
      {periods.map((p, i) => {
        const val = accessor(p);
        return (
          <div key={i} className="flex-1 min-w-0">
            <p className="text-xl font-bold truncate" style={{ color: PERIOD_COLORS[i] }}>
              {formatter ? formatter(val) : (typeof val === 'number' ? val.toLocaleString() : val)}
            </p>
            <p className="text-[10px] font-medium mt-0.5 truncate" style={{ color: PERIOD_COLORS[i] }}>{p.label}</p>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Grouped Bar Chart ──────────────────
const GroupedBarChart = ({ title, periods, field, section = 'lead_metrics' }) => {
  const allNames = new Set();
  periods.forEach(p => {
    const m = p[section] || {};
    (m[field] || []).forEach(d => allNames.add(d.name));
  });
  const chartData = [...allNames].slice(0, 10).map(name => {
    const row = { name };
    periods.forEach((p, i) => {
      const m = p[section] || {};
      const match = (m[field] || []).find(d => d.name === name);
      row[`p${i}`] = match?.count || 0;
    });
    return row;
  });
  if (!chartData.length) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <h4 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">{title}</h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          {periods.map((p, i) => (
            <Bar key={i} dataKey={`p${i}`} name={p.label} fill={PERIOD_COLORS[i]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Lead Information Comparison ──────────────────
const LeadInfoComparison = ({ periods }) => (
  <div className="space-y-4" data-testid="tc-lead-info">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiCompare title="Total Leads" icon={Users} periods={periods} accessor={p => p.lead_metrics?.total || 0} />
      <KpiCompare title="Closed Won" icon={CheckCircle} periods={periods} accessor={p => p.lead_metrics?.closed_won || 0} />
      <KpiCompare title="Revenue" icon={TrendingUp} periods={periods}
        accessor={p => p.lead_metrics?.revenue || 0} formatter={v => `₹${v.toLocaleString()}`} />
      <KpiCompare title="OTA Active" icon={Wifi} periods={periods} accessor={p => p.lead_metrics?.ota_active || 0} />
      <KpiCompare title="Payment Done" icon={CreditCard} periods={periods} accessor={p => p.lead_metrics?.payment_done || 0} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <GroupedBarChart title="By Lead Stage" periods={periods} field="by_stage" section="lead_metrics" />
      <GroupedBarChart title="By Source" periods={periods} field="by_source" section="lead_metrics" />
      <GroupedBarChart title="By Owner" periods={periods} field="by_owner" section="lead_metrics" />
      <GroupedBarChart title="By City" periods={periods} field="by_city" section="lead_metrics" />
    </div>
  </div>
);

// ─── Geographical Comparison ──────────────────
const GeoComparison = ({ periods }) => (
  <div className="space-y-4" data-testid="tc-geo">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <KpiCompare title="Total Properties" icon={Building2} periods={periods} accessor={p => p.property_metrics?.total || 0} />
      <KpiCompare title="Total Rooms" icon={Layers} periods={periods} accessor={p => p.property_metrics?.total_rooms || 0} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <GroupedBarChart title="By State" periods={periods} field="by_state" section="property_metrics" />
      <GroupedBarChart title="By City" periods={periods} field="by_city" section="property_metrics" />
      <GroupedBarChart title="By Type" periods={periods} field="by_type" section="property_metrics" />
      <GroupedBarChart title="By Status" periods={periods} field="by_status" section="property_metrics" />
    </div>
  </div>
);

// ─── Offboarded Comparison ──────────────────
const OffboardedComparison = ({ periods }) => (
  <div className="space-y-4" data-testid="tc-offboarded">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <KpiCompare title="Total Offboarded" icon={AlertCircle} periods={periods} accessor={p => p.offboarded_metrics?.total || 0} />
      <KpiCompare title="Completed" icon={CheckCircle} periods={periods} accessor={p => p.offboarded_metrics?.completed || 0} />
      <KpiCompare title="In Progress" icon={MapPin} periods={periods} accessor={p => p.offboarded_metrics?.in_progress || 0} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <GroupedBarChart title="By Reason" periods={periods} field="by_reason" section="offboarded_metrics" />
      <GroupedBarChart title="By City" periods={periods} field="by_city" section="offboarded_metrics" />
    </div>
  </div>
);

// ─── Main export ──────────────────
const TimeComparisonResults = ({ periods, activeTab }) => {
  if (!periods || periods.length === 0) return null;

  // Period legend
  const legend = (
    <div className="flex items-center gap-4 mb-4 px-1">
      {periods.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERIOD_COLORS[i] }} />
          <span className="text-xs font-semibold" style={{ color: PERIOD_COLORS[i] }}>{p.label}</span>
          <span className="text-[10px] text-gray-400">({p.from} — {p.to})</span>
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="time-comparison-results">
      {legend}
      {activeTab === 'lead-info' && <LeadInfoComparison periods={periods} />}
      {activeTab === 'geographical' && <GeoComparison periods={periods} />}
      {activeTab === 'offboarded' && <OffboardedComparison periods={periods} />}
    </div>
  );
};

export default TimeComparisonResults;
